import React, { useMemo } from 'react';

// ==================== MUI Material & External Libraries ====================
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    Typography,
    useTheme
} from '@mui/material';

import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// ==================== Icons ====================
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import {
    CompleteChipIcon,
    InprogressChipIcon,
    ModernTATFormIncon,
    PendingChipIcon
} from 'utils/TaskManagementIcon/taskmanagemeneticon';

import { showError } from 'utils/errorHandler';
import { ModernCompletedIcon } from 'utils/icons';
import { minDate, minDateFormated } from 'utils/commonFunctions';
import { deriveStatusFromDates, parseDateClean } from 'utils/tatUtils';
import { useUpdateElapsedMinutes } from 'api/TaskManagement/taskManagement';
import { UpdateElapsedMinutesPayload } from 'types/taskManagement/TaskManagement';
import TatChip from 'components/TaskManagement/TatChip';

interface TATFormProps {
    open: boolean;
    onClose: () => void;
    task: any;
    onSuccess?: (values?: { startedOn?: string | null; completedOn?: string | null; updatedOn?: string | null }) => void;
}

interface FormValues {
    startedDate: Dayjs | null;
    startedTime: Dayjs | null;
    completedDate: Dayjs | null;
    completedTime: Dayjs | null;
    submit?: string;
}

export const TATForm: React.FC<TATFormProps> = ({ open, onClose, task, onSuccess }) => {
    const theme = useTheme();

    const initialStartedDateTime = parseDateClean(task?.startedOn);
    const initialCompletedDateTime = parseDateClean(task?.completedOn);

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: {
            startedDate: initialStartedDateTime,
            startedTime: initialStartedDateTime,
            completedDate: initialCompletedDateTime,
            completedTime: initialCompletedDateTime
        },
        validationSchema: Yup.object({
            startedDate: Yup.mixed()
                .nullable()
                .test('is-valid-date', 'Please enter a valid started date', (value) => {
                    if (!value) return true;
                    return dayjs(value as any).isValid();
                })
                .test('min-date', `Started date cannot be earlier than ${minDateFormated}`, (value) => {
                    if (!value) return true;
                    if (!dayjs(value as any).isValid()) return true;
                    return !dayjs(value as any).isBefore(dayjs(minDate), 'day');
                })
                .test('required-when-completed', 'Started On is required when Completed On is entered.', function (value) {
                    const { completedDate, completedTime } = this.parent;
                    if ((completedDate || completedTime) && !value) {
                        return false;
                    }
                    return true;
                })
                .test('required-when-time', 'Started date is required when started time is entered.', function (value) {
                    const { startedTime } = this.parent;
                    if (startedTime && !value) {
                        return false;
                    }
                    return true;
                }),
            startedTime: Yup.mixed()
                .nullable()
                .test('is-valid-time', 'Please enter a valid started time', (val) => {
                    if (!val) return true;
                    return dayjs(val as any).isValid();
                })
                .test('required-when-date', 'Started time is required when started date is entered.', function (value) {
                    const { startedDate } = this.parent;
                    if (startedDate && !value) {
                        return false;
                    }
                    return true;
                })
                .test('required-when-completed', 'Started On is required when Completed On is entered.', function (value) {
                    const { completedDate, completedTime } = this.parent;
                    if ((completedDate || completedTime) && !value) {
                        return false;
                    }
                    return true;
                }),
            completedDate: Yup.mixed()
                .nullable()
                .test('is-valid-date', 'Please enter a valid completed date', (value) => {
                    if (!value) return true;
                    return dayjs(value as any).isValid();
                })
                .test('min-date', `Completed date cannot be earlier than ${minDateFormated}`, (value) => {
                    if (!value) return true;
                    if (!dayjs(value as any).isValid()) return true;
                    return !dayjs(value as any).isBefore(dayjs(minDate), 'day');
                })
                .test('min-started-date', function (value) {
                    const { startedDate } = this.parent;
                    if (!value || !startedDate) return true;
                    if (!dayjs(value as any).isValid() || !dayjs(startedDate as any).isValid()) return true;
                    if (dayjs(value as any).isBefore(dayjs(startedDate as any), 'day')) {
                        return this.createError({
                            message: 'Completed On cannot be before Started On.'
                        });
                    }
                    return true;
                })
                .test('max-365-days', function (value) {
                    const { startedDate } = this.parent;
                    if (!value || !startedDate) return true;
                    if (!dayjs(value as any).isValid() || !dayjs(startedDate as any).isValid()) return true;
                    const diffDays = dayjs(value as any).diff(dayjs(startedDate as any), 'day', true);
                    if (diffDays > 365) {
                        return this.createError({
                            message: 'The time between Started On and Completed On cannot be more than 365 days.'
                        });
                    }
                    return true;
                }),
            completedTime: Yup.mixed()
                .nullable()
                .test('is-valid-time', 'Please enter a valid completed time', (val) => {
                    if (!val) return true;
                    return dayjs(val as any).isValid();
                })
                .test('required-when-date', 'Completed time is required when completed date is entered.', function (value) {
                    const { completedDate } = this.parent;
                    if (completedDate && !value) {
                        return false;
                    }
                    return true;
                })
                .test('min-time-and-max-range', function (value) {
                    const { startedDate, startedTime, completedDate } = this.parent;
                    if (!value || !startedDate || !startedTime || !completedDate) return true;
                    if (!dayjs(value as any).isValid() || !dayjs(startedDate as any).isValid() || !dayjs(startedTime as any).isValid() || !dayjs(completedDate as any).isValid()) return true;

                    const startTimeVal = dayjs(startedTime as any);
                    const completedTimeVal = dayjs(value as any);
                    const start = dayjs(startedDate as any).hour(startTimeVal.hour()).minute(startTimeVal.minute()).second(0);
                    const end = dayjs(completedDate as any).hour(completedTimeVal.hour()).minute(completedTimeVal.minute()).second(0);
                    if (end.isBefore(start)) {
                        return this.createError({
                            message: 'Completed On cannot be before Started On.'
                        });
                    }
                    const diffDays = end.diff(start, 'day', true);
                    if (diffDays > 365) {
                        return this.createError({
                            message: 'The time between Started On and Completed On cannot be more than 365 days.'
                        });
                    }
                    return true;
                })
        }),
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            try {
                const hasStarted = Boolean(values.startedDate && values.startedTime);
                const hasCompleted = Boolean(values.completedDate && values.completedTime);

                // Validation 1: Started On is required when Completed On is entered
                if (!hasStarted && (values.completedDate || values.completedTime)) {
                    setErrors({ startedDate: 'Started On is required when Completed On is entered.' });
                    setSubmitting(false);
                    return;
                }

                let start: Dayjs | null = null;
                if (hasStarted && values.startedDate) {
                    start = values.startedDate;
                    if (values.startedTime) {
                        start = start
                            .hour(values.startedTime.hour())
                            .minute(values.startedTime.minute())
                            .second(values.startedTime.second())
                            .millisecond(values.startedTime.millisecond());
                    } else {
                        start = start.hour(0).minute(0).second(0).millisecond(0);
                    }
                }

                let end: Dayjs | null = null;
                if (hasCompleted && values.completedDate) {
                    end = values.completedDate;
                    if (values.completedTime) {
                        end = end
                            .hour(values.completedTime.hour())
                            .minute(values.completedTime.minute())
                            .second(values.completedTime.second())
                            .millisecond(values.completedTime.millisecond());
                    } else {
                        end = end.hour(0).minute(0).second(0).millisecond(0);
                    }
                }

                if (start && end) {
                    // Validation 2: Completed On cannot be before Started On
                    if (end.isBefore(start)) {
                        setErrors({ completedDate: 'Completed On cannot be before Started On.' });
                        setSubmitting(false);
                        return;
                    }

                    // Validation 3: Time between Started On and Completed On cannot be more than 365 days
                    const diffDays = end.diff(start, 'day', true);
                    if (diffDays > 365) {
                        setErrors({ completedDate: 'The time between Started On and Completed On cannot be more than 365 days.' });
                        setSubmitting(false);
                        return;
                    }
                }

                const startedOn = start && start.isValid() ? start.format('YYYY-MM-DD HH:mm:ss.SSS') : null;
                const completedOn = end && end.isValid() ? end.format('YYYY-MM-DD HH:mm:ss.SSS') : null;

                // Payload structure matching backend API:
                // PUT /Task/UpdateElapsedMinutes
                const payload: UpdateElapsedMinutesPayload = {
                    updatedOn: task?.updatedOn ?? null,
                    taskID: Number(task?.taskID || task?.id),
                    startedOn,
                    completedOn
                };

                const res = await useUpdateElapsedMinutes(payload);

                if (onSuccess) {
                    onSuccess({
                        startedOn,
                        completedOn,
                        updatedOn: res?.updatedOn || res?.data?.updatedOn || (typeof res?.data === 'string' ? res.data : null)
                    });
                }
                onClose();
            } catch (err: any) {
                setErrors({ submit: err.message });
            } finally {
                setSubmitting(false);
            }
        }
    });

    const { values, errors, touched, handleSubmit, setFieldValue, setFieldTouched, isSubmitting } = formik;

    // Derived read-only New Status logic
    const derivedNewStatus = useMemo(() => {
        const hasStarted = Boolean(values.startedDate && values.startedTime);
        const hasCompleted = Boolean(values.completedDate && values.completedTime);

        // If completed is filled but started is missing -> Invalid
        if (!hasStarted && (values.completedDate || values.completedTime)) {
            return {
                label: 'Invalid',
                color: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                icon: <CloseOutlined style={{ fontSize: 13, color: theme.palette.error.main }} />
            };
        }

        // If neither started nor completed is filled -> Pending
        if (!hasStarted && !hasCompleted) {
            return {
                label: 'Pending',
                color: theme.palette.warning.main,
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                icon: <PendingChipIcon style={{ width: 15, height: 15 }} />
            };
        }

        // If both started and completed are completely filled -> Completed
        if (hasStarted && hasCompleted) {
            return {
                label: 'Completed',
                color: theme.palette.success.main,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                icon: <ModernCompletedIcon style={{ width: 15, height: 15, color: theme.palette.success.main }} />
            };
        }

        // Started is filled, completed is still empty or in progress -> In-Progress
        return {
            label: 'In-Progress',
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
            icon: <InprogressChipIcon style={{ width: 15, height: 15 }} />
        };
    }, [values.startedDate, values.startedTime, values.completedDate, values.completedTime, theme]);

    // Current status display configuration
    const currentStatus = task?.status;
    const currentStatusDisplay = useMemo(() => {
        if (currentStatus === 'Completed') {
            return {
                label: 'Completed',
                color: theme.palette.success.main,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                icon: <ModernCompletedIcon style={{ width: 15, height: 15, color: theme.palette.success.main }} />
            };
        }
        if (currentStatus === 'Pending') {
            return {
                label: 'Pending',
                color: theme.palette.warning.main,
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                icon: <PendingChipIcon style={{ width: 15, height: 15 }} />
            };
        }
        return {
            label: currentStatus,
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
            icon: <InprogressChipIcon style={{ width: 15, height: 15 }} />
        };
    }, [currentStatus, theme]);

    // Derived strings for TatChip
    const derivedStartedOn = useMemo(() => {
        if (!values.startedDate) return null;
        let start = dayjs(values.startedDate);
        if (values.startedTime) {
            const timeVal = dayjs(values.startedTime);
            start = start
                .hour(timeVal.hour())
                .minute(timeVal.minute())
                .second(0)
                .millisecond(0);
        } else {
            start = start.hour(0).minute(0).second(0).millisecond(0);
        }
        return start.isValid() ? start.format('YYYY-MM-DDTHH:mm:ss') : null;
    }, [values.startedDate, values.startedTime]);

    const derivedCompletedOn = useMemo(() => {
        if (!values.completedDate || !values.completedTime) return null;
        const timeVal = dayjs(values.completedTime);
        const end = dayjs(values.completedDate)
            .hour(timeVal.hour())
            .minute(timeVal.minute())
            .second(0)
            .millisecond(0);
        return end.isValid() ? end.format('YYYY-MM-DDTHH:mm:ss') : null;
    }, [values.completedDate, values.completedTime]);

    const taskTitle = task?.title || task?.taskName || task?.name;

    const isFutureStart = Boolean(
        derivedStartedOn &&
        !derivedCompletedOn &&
        dayjs(derivedStartedOn).isValid() &&
        dayjs(derivedStartedOn).isAfter(dayjs())
    );
    const futureStartFormatted = isFutureStart
        ? dayjs(derivedStartedOn).isSame(dayjs(), 'day')
            ? dayjs(derivedStartedOn).format('hh:mm A')
            : dayjs(derivedStartedOn).format('MM/DD/YYYY hh:mm A')
        : '';

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 1 }
            }}
        >
            {/* Dialog Header */}
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="span">
                    <Typography component="span" sx={{ color: 'primary.main' }}>
                        Update
                    </Typography>{' '}
                    Elapsed Time (ET)
                </Typography>
                <IconButton
                    aria-label="close"
                    disabled={isSubmitting}
                    onClick={onClose}
                    sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.lighter' }
                    }}
                >
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <Divider />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <DialogContent sx={{ p: 2.5 }}>
                            {/* Task Title Row */}
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2.5 }}>

                                <ModernTATFormIncon />

                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        color: 'primary.main',
                                        lineHeight: 1
                                    }}
                                >
                                    {taskTitle}
                                </Typography>
                            </Stack>

                            {/* Status and Turn Around Time Summary Row */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2.5}
                                divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', height: 55, my: 'auto' }} />}
                                sx={{ mb: 1.5 }}
                            >
                                {/* Current Status */}
                                <Stack spacing={1} sx={{ minWidth: 110 }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 400, color: 'text.secondary' }}>
                                        Current Status
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            height: 30,
                                            px: 1.25,
                                            borderRadius: '6px',
                                            bgcolor: currentStatusDisplay.bgcolor,
                                            border: currentStatusDisplay.border,
                                            width: 'fit-content',

                                        }}
                                    >
                                        {currentStatusDisplay.icon}
                                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: currentStatusDisplay.color, lineHeight: 1 }}>
                                            {currentStatusDisplay.label}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {/* New Status (Derived Read-Only Badge) */}
                                <Stack spacing={0.75} sx={{ minWidth: 110 }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 400, color: 'text.secondary' }}>
                                        New Status
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            height: 29,
                                            px: 1.25,
                                            borderRadius: '6px',
                                            bgcolor: derivedNewStatus.bgcolor,
                                            border: derivedNewStatus.border,
                                            width: 'fit-content'
                                        }}
                                    >
                                        {derivedNewStatus.icon}
                                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: derivedNewStatus.color, lineHeight: 1 }}>
                                            {derivedNewStatus.label}
                                        </Typography>
                                    </Box>
                                </Stack>

                                {/* Turn Around Time Display (Shown for Completed & In-Progress, Hidden for Pending/Invalid) */}
                                {(derivedNewStatus.label === 'In-Progress' || derivedNewStatus.label === 'Completed') && derivedStartedOn && (
                                    <Stack spacing={0.75} sx={{ minWidth: 110 }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 400, color: 'text.secondary' }}>
                                            Elapsed Time
                                        </Typography>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 29, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                                            <TatChip
                                                startedOn={derivedStartedOn}
                                                completedOn={derivedCompletedOn}
                                                variant='text'
                                                showIcon={false}
                                                sx={{ fontSize: '15px', color: 'text.secondary' }}
                                            />
                                            {isFutureStart && (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '12.5px',
                                                        color: 'text.secondary',
                                                        fontStyle: 'italic',
                                                        ml: 0.75,
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {`(Starts at ${futureStartFormatted})`}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                )}
                            </Stack>

                            <Divider sx={{ mb: 2.5 }} />

                            {/* Started On Section */}
                            <Box sx={{ mb: 2.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary', mb: 1, fontSize: '14px' }}>
                                    Started On
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* Started Date */}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Date</InputLabel>
                                            <DatePicker
                                                value={values.startedDate}
                                                minDate={dayjs(minDate)}
                                                onChange={(newValue) => {
                                                    setFieldValue('startedDate', newValue);
                                                    setFieldTouched('startedDate', true, false);
                                                    // Auto update completed date if it is before started date
                                                    if (values.completedDate && newValue && values.completedDate.isBefore(newValue, 'day')) {
                                                        setFieldValue('completedDate', newValue);
                                                        setFieldTouched('completedDate', true, false);
                                                    }
                                                }}
                                                format="MM/DD/YYYY"
                                                enableAccessibleFieldDOMStructure={false}
                                                disabled={isSubmitting}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        size: 'medium',
                                                        placeholder: 'MM/DD/YYYY',
                                                        error: Boolean(touched.startedDate && errors.startedDate),
                                                        helperText: touched.startedDate && (errors.startedDate as string),
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </Grid>

                                    {/* Started Time */}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Time</InputLabel>
                                            <TimePicker
                                                value={values.startedTime}
                                                onChange={(newValue) => {
                                                    setFieldValue('startedTime', newValue);
                                                    setFieldTouched('startedTime', true, false);
                                                }}
                                                format="hh:mm A"
                                                enableAccessibleFieldDOMStructure={false}
                                                disabled={isSubmitting}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        size: 'medium',
                                                        placeholder: 'hh:mm AM/PM',
                                                        error: Boolean(touched.startedTime && errors.startedTime),
                                                        helperText: touched.startedTime && (errors.startedTime as string),
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Completed On Section */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, fontSize: '14px' }}>
                                    Completed On
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* Completed Date with minDate */}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Date</InputLabel>
                                            <DatePicker
                                                value={values.completedDate}
                                                minDate={values.startedDate ? dayjs(values.startedDate) : dayjs(minDate)}
                                                onChange={(newValue) => {
                                                    setFieldValue('completedDate', newValue);
                                                    setFieldTouched('completedDate', true, false);
                                                }}
                                                format="MM/DD/YYYY"
                                                enableAccessibleFieldDOMStructure={false}
                                                disabled={isSubmitting}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        size: 'medium',
                                                        placeholder: 'MM/DD/YYYY',
                                                        error: Boolean(touched.completedDate && errors.completedDate),
                                                        helperText: touched.completedDate && (errors.completedDate as string),
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </Grid>

                                    {/* Completed Time */}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Time</InputLabel>
                                            <TimePicker
                                                value={values.completedTime}
                                                onChange={(newValue) => {
                                                    setFieldValue('completedTime', newValue);
                                                    setFieldTouched('completedTime', true, false);
                                                }}
                                                format="hh:mm A"
                                                enableAccessibleFieldDOMStructure={false}
                                                disabled={isSubmitting}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        size: 'medium',
                                                        placeholder: 'hh:mm AM/PM',
                                                        error: Boolean(touched.completedTime && errors.completedTime),
                                                        helperText: touched.completedTime && (errors.completedTime as string),
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </DialogContent>

                        <Divider />

                        {/* Dialog Footer Actions */}
                        <DialogActions sx={{ p: 2, px: 2.5, justifyContent: 'flex-end', gap: 1.5 }}>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting || !formik.isValid || derivedNewStatus.label === 'Invalid'}
                                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                                sx={{
                                    borderRadius: '6px',
                                    px: 2.5,
                                    py: 0.8,
                                    textTransform: 'none',
                                    fontWeight: 500
                                }}
                            >
                                Save
                            </Button>
                        </DialogActions>
                    </Form>
                </FormikProvider>
            </LocalizationProvider>
        </Dialog>
    );
};

export default TATForm;